import React from 'react';

import ReactDOM from 'react-dom';

import { getCurrentContentProperties } from '@lumapps/contents/ducks/selectors';
import { actions as contentActions } from '@lumapps/contents/ducks/slice';
import { useDispatch, useSelector } from '@lumapps/redux/react';
import { useRouter } from '@lumapps/router';
import { BaseLoadingStatus } from '@lumapps/utils/types/BaseLoadingStatus';
import { Layout } from '@lumapps/widget-layout/components/Layout';
import type * as types from '@lumapps/widget-layout/types';

import * as api from '../../api';
import { fetchContentLayout } from '../../ducks/thunks';
import { ContentLayoutProps } from '../../types';

/**
 * Render content layout
 */
export const ContentLayout: React.FC<ContentLayoutProps> = (props) => {
    const { shouldRedirectOnFail = true, contentId, enabledWidgetTypes, onStatusChange, skipContentHeader } = props;

    const dispatch = useDispatch();
    const { redirect } = useRouter();
    const [layout, setLayout] = React.useState<types.Layout | undefined>();

    // Fetch on mount
    React.useEffect(
        () => {
            onStatusChange?.(BaseLoadingStatus.loading);

            // `requestAnimationFrame` is needed because we cannot execute `flushSync` during a render.
            requestAnimationFrame(async () => {
                /**
                 * Destroy the current layout before fetching the new one
                 * The fetchContentLayout method contains the methods to update the store with the new layout.
                 * We need to clean the previous layout before settings the new properties to avoid side effects.
                 * `flushSync` is needed to make sure we apply the layout reset immediately.
                 */
                ReactDOM.flushSync(() => {
                    setLayout(undefined);
                });
                const layout = await dispatch(
                    fetchContentLayout({
                        contentId,
                        fetch: api.getContentLayoutById,
                        enabledWidgetTypes,
                        // Do not provide the redirect method which is used in error cases
                        redirect: shouldRedirectOnFail ? redirect : undefined,
                        // Do not fetch the content header
                        skipContentHeader,
                    }),
                );
                onStatusChange?.(BaseLoadingStatus.idle);
                setLayout(layout as any);
            });

            // Cancel fetch on unmount (& id change)
            return () => {
                api.cancelFetch(contentId);
            };
        },
        // Fetch only once on content id change
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [contentId],
    );

    React.useEffect(() => {
        // Reset content on unmount
        return () => {
            dispatch(contentActions.resetContent());
        };
    }, [dispatch]);

    const properties = useSelector(getCurrentContentProperties);

    return <Layout {...layout} {...props} properties={properties} />;
};

export default ContentLayout;

export type { ContentLayoutProps };
